import axios from "axios";
import { getSessionGeneration } from "./sessionGeneration";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });
let csrfPromise;
let refreshPromise;

// Debounced broadcast so a burst of mutations coalesces into one /auth/me.
let walletRefreshTimer;
const scheduleWalletRefresh = () => {
  clearTimeout(walletRefreshTimer);
  walletRefreshTimer = setTimeout(() => {
    window.dispatchEvent(new Event("playden-wallet-refresh"));
  }, 400);
};

export const getCsrfToken = () => {
  if (!csrfPromise) {
    csrfPromise = axios.get(`${API}/auth/csrf`, { withCredentials: true })
      .then(({ data }) => data.csrf_token)
      .catch((error) => { csrfPromise = undefined; throw error; });
  }
  return csrfPromise;
};

api.interceptors.request.use(async (config) => {
  config._sessionGeneration ??= getSessionGeneration();
  if (!["get", "head", "options"].includes((config.method || "get").toLowerCase())) {
    config.headers["X-CSRF-Token"] = await getCsrfToken();
  }
  return config;
});

api.interceptors.response.use(
  (r) => {
    // Site-wide "instant balance" updater: any successful state-changing
    // request may have moved the player's credits/SP, so nudge a single,
    // debounced wallet refresh (AuthContext listens). Reads never fire it,
    // and auth/presence are excluded (they can't change the balance and
    // presence heartbeats would otherwise poll /auth/me every 15s).
    const cfg = r.config || {};
    const method = (cfg.method || "get").toLowerCase();
    if (["post", "put", "patch", "delete"].includes(method)) {
      const raw = cfg.url || "";
      const path = raw.startsWith("http") ? raw.slice(API.length) : raw;
      if (!path.startsWith("/auth") && !path.startsWith("/presence")) scheduleWalletRefresh();
    }
    return r;
  },
  async (err) => {
    const config = err.config;
    if (config?._sessionGeneration !== getSessionGeneration()) return Promise.reject(err);
    if (err?.response?.status === 403 && err.response.data?.detail === "CSRF verification failed" && !config?._csrfRetry) {
      csrfPromise = undefined;
      return api({ ...config, _csrfRetry: true });
    }
    // Never repeat a wrong-password/PIN attempt. Only expired access cookies refresh.
    if (err?.response?.status === 401 && err.response.data?.detail === "Session expired" && !config?._sessionRetry && !config?._skipSessionRefresh && config?.url !== "/auth/refresh") {
      const generation = config._sessionGeneration;
      if (!refreshPromise || refreshPromise.generation !== generation) {
        const pending = { generation };
        pending.promise = api.post("/auth/refresh").finally(() => { if (refreshPromise === pending) refreshPromise = undefined; });
        refreshPromise = pending;
      }
      try {
        await refreshPromise.promise;
        if (generation === getSessionGeneration()) return api({ ...config, _sessionRetry: true });
      } catch {
        if (generation === getSessionGeneration()) window.dispatchEvent(new Event("playden-session-expired"));
      }
    }
    return Promise.reject(err);
  }
);

export function apiError(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  // Common shapes:
  //   • FastAPI 422 → array of { type, loc, msg, input, url } objects
  //   • Custom raise HTTPException → plain string
  //   • Anything else → fall back to axios error message
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === "string") return d;
        const field = Array.isArray(d?.loc) ? d.loc.filter((seg) => seg !== "body").join(".") : null;
        const msg = d?.msg || d?.message || "Invalid";
        return field ? `${field}: ${msg}` : msg;
      })
      .join(" · ");
  }
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return err?.message || fallback;
}
